import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const INVESTOR_URL = process.env.REACT_APP_INVESTOR_URL || 'https://invest.we4task.com';

const PublicHeader: React.FC = () => {
    const location = useLocation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isLoginDropdownOpen, setIsLoginDropdownOpen] = useState(false);
    const mobileMenuRef = useRef<HTMLDivElement>(null);
    const menuButtonRef = useRef<HTMLButtonElement>(null);
    const backdropRef = useRef<HTMLDivElement>(null);
    const loginDropdownRef = useRef<HTMLDivElement>(null);

    const isActive = (path: string) => {
        return location.pathname === path;
    };

    useEffect(() => {
        setIsMobileMenuOpen(false);
        setIsLoginDropdownOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
    }, [isMobileMenuOpen]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (loginDropdownRef.current && !loginDropdownRef.current.contains(event.target as Node)) {
                setIsLoginDropdownOpen(false);
            }
        };

        if (isLoginDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isLoginDropdownOpen]);

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(prev => !prev);
    };

    const closeMobileMenu = () => {
        setIsMobileMenuOpen(false);
    };

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === backdropRef.current) {
            closeMobileMenu();
        }
    };

    const navLinks = [
        { path: '/', label: 'Home' },
        { path: '/about', label: 'About' },
        { path: '/services', label: 'Services' },
        { path: '/projects', label: 'Projects' },
        { path: '/partners', label: 'Partners' },
        { path: '/contact', label: 'Contact' },
        { path: '/career', label: 'Career' },
    ];

    return (
        <>
            <header className="fixed top-0 w-full z-50 bg-gray-900 border-b border-gray-800 shadow-sm">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-4">
                        <Link
                            to="/"
                            className="flex items-center"
                            onClick={closeMobileMenu}
                        >
                            <img
                                src="/logo.png"
                                alt="We4Task Logo"
                                className="h-10 w-auto"
                            />
                        </Link>

                        <nav className="hidden lg:flex items-center space-x-6">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    className={`${isActive(link.path) ? 'text-[#3B82F6] font-semibold' : 'text-gray-300 hover:text-[#3B82F6]'} text-base font-medium transition-colors`}
                                >
                                    {link.label}
                                </Link>
                            ))}
                        </nav>

                        <div className="flex items-center space-x-4">
                            {/* Desktop Login Dropdown */}
                            <div className="hidden sm:block relative" ref={loginDropdownRef}>
                                <button
                                    onClick={() => setIsLoginDropdownOpen(!isLoginDropdownOpen)}
                                    className="bg-[#3B82F6] text-white px-5 py-2 rounded-md hover:bg-blue-700 transition-colors font-medium text-base flex items-center space-x-1"
                                >
                                    <span>Login</span>
                                    <svg
                                        className={`w-4 h-4 transition-transform ${isLoginDropdownOpen ? 'rotate-180' : ''}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>

                                {isLoginDropdownOpen && (
                                    <div className="absolute right-0 mt-2 w-48 bg-gray-800 rounded-md shadow-lg border border-gray-700 py-1 z-50">
                                        <Link
                                            to="/login"
                                            onClick={() => setIsLoginDropdownOpen(false)}
                                            className="block px-4 py-2 text-gray-300 hover:bg-gray-700 hover:text-[#3B82F6] transition-colors"
                                        >
                                            CRM Login
                                        </Link>
                                        <a
                                            href={INVESTOR_URL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block px-4 py-2 text-gray-300 hover:bg-gray-700 hover:text-[#3B82F6] transition-colors"
                                        >
                                            Investor Login
                                        </a>
                                    </div>
                                )}
                            </div>

                            <button
                                ref={menuButtonRef}
                                onClick={toggleMobileMenu}
                                className="lg:hidden p-2 text-gray-300 hover:text-[#3B82F6] focus:outline-none"
                                aria-label="Toggle menu"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    {isMobileMenuOpen ? (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    ) : (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                    )}
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {isMobileMenuOpen && (
                <>
                    <div
                        ref={backdropRef}
                        onClick={handleBackdropClick}
                        className="lg:hidden fixed inset-0 top-[73px] z-40 bg-black/50"
                    />

                    <div
                        ref={mobileMenuRef}
                        className="lg:hidden fixed top-[73px] right-0 bottom-0 w-64 z-50 bg-gray-900 shadow-xl overflow-y-auto"
                    >
                        <nav className="flex flex-col py-4">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    onClick={closeMobileMenu}
                                    className={`${isActive(link.path) ? 'text-[#3B82F6] bg-gray-800 border-l-4 border-[#3B82F6]' : 'text-gray-300 hover:bg-gray-800 border-l-4 border-transparent'} px-6 py-3 text-base font-medium transition-colors`}
                                >
                                    {link.label}
                                </Link>
                            ))}

                            <div className="px-6 py-4 mt-2 border-t border-gray-800 space-y-2">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Login Options</p>
                                <Link
                                    to="/login"
                                    onClick={closeMobileMenu}
                                    className="block w-full text-center bg-[#3B82F6] text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors font-medium text-base"
                                >
                                    CRM Login
                                </Link>
                                <a
                                    href={INVESTOR_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={closeMobileMenu}
                                    className="block w-full text-center bg-gray-900 border-2 border-[#3B82F6] text-[#3B82F6] px-4 py-2 rounded-md hover:bg-gray-800 transition-colors font-medium text-base"
                                >
                                    Investor Login
                                </a>
                            </div>
                        </nav>
                    </div>
                </>
            )}
        </>
    );
};

export default PublicHeader;
